import { FC, memo, useCallback, useMemo } from 'react'

import { AccordionNoRadix, ArrowRightIcon, Text, Button } from '@cash-me/react-components'
import { useRouter } from 'next/router'

import { Grid, Col } from 'components/grid'
import { Polygon } from 'components/polygon'
import useWindowSize from 'hooks/useWindowSize'
import { theme } from 'styles/theme'
import { buildSimulatorUrl } from 'utils/buildSimulatorUrl'
import { pushDataLayer } from 'utils/push-data-layer'

import type { HelpProps, AccordionProps, LinkItemProps } from './help.model'
import { HelpItemRenderer } from './HelpItemRenderers'
import { DEFAULT_COLORS, CONTRAST_COLORS } from './HelpTheme'
import * as S from './styles'

export const Help: FC<HelpProps> = memo(
  ({
    children,
    accordion,
    $bg,
    $previousBG = 'neutral-background-medium',
    onClick,
    buttonAlign = 'center',
    buttonAriaLabel,
    buttonLabel = 'Simular agora',
    showButtonArrow = true,
    layoutVariant = 'default',
    longFormMinHeight = '1254px'
  }) => {
    const router = useRouter()
    const { isTablet } = useWindowSize()
    const isLongForm = layoutVariant === 'long-form'
    const { titleTextColor, itemTextColor, accordionArrowColor } = useMemo(
      () => ($bg === 'contrast' ? CONTRAST_COLORS : DEFAULT_COLORS),
      [$bg]
    )

    const handleAccordionClick = useCallback((title: string) => {
      pushDataLayer({
        category: 'Clique',
        action: 'Ajuda',
        label: `Accordion Pergunta: ${title}`
      })
    }, [])

    const handleLinkItemClick = useCallback(
      (content: LinkItemProps) => {
        const { dataLayer, href, label } = content

        pushDataLayer(dataLayer)

        if (href === 'simulador-home-equity') {
          location.href = buildSimulatorUrl(router.pathname, { medium: 'Ajuda - ' + label })
        } else {
          router.push(href)
        }
      },
      [router]
    )

    return (
      <S.Section
        $bg={$bg}
        $buttonAlign={buttonAlign}
        $layoutVariant={layoutVariant}
        $longFormMinHeight={longFormMinHeight}
      >
        <Grid col={{ phone: 1, tablet: 2 }}>
          <Col col={{ desktop: 4 }} start={{ desktop: 2 }}>
            <S.Infos
              $previousBG={$previousBG}
              $layoutVariant={layoutVariant}
              $longFormMinHeight={longFormMinHeight}
            >
              <Polygon
                type="TriangleRound"
                width={isLongForm ? 66 : 80}
                height={isLongForm ? 80 : 100}
                draw="fill"
                color={theme.colors['cashme-magenta']}
                className="background-polygon polygon-pink"
              />
              {children}
              {isTablet && (
                <Button aria-label={buttonAriaLabel} size="lg" onClick={onClick} variants="text-only">
                  {buttonLabel}
                  {isLongForm && showButtonArrow ? (
                    <ArrowRightIcon aria-hidden color="var(--colors-primary-icon-strong)" size={24} />
                  ) : null}
                </Button>
              )}
            </S.Infos>
          </Col>
          <Col col={{ desktop: 5 }} start={{ tablet: 2, desktop: 7 }}>
            <S.AccordionWrapper $bg={$bg} $layoutVariant={layoutVariant}>
              {accordion.map(({ id, title, item }: AccordionProps) => (
                <div key={id} onClick={() => handleAccordionClick(title)}>
                  <AccordionNoRadix
                    id={id}
                    title={
                      <Text as="h3" size={isLongForm ? 'heading5' : 'heading4'} weight="medium" color={titleTextColor}>
                        {title}
                      </Text>
                    }
                  >
                    {item.map(itemData => (
                      <HelpItemRenderer
                        key={itemData.id}
                        item={itemData}
                        itemTextColor={itemTextColor}
                        accordionArrowColor={accordionArrowColor}
                        onLinkClick={handleLinkItemClick}
                      />
                    ))}
                  </AccordionNoRadix>
                </div>
              ))}
            </S.AccordionWrapper>
            {!isTablet && (
              <Button aria-label={buttonAriaLabel} size="lg" onClick={onClick} variants="text-only">
                {buttonLabel}
                {isLongForm && showButtonArrow ? (
                  <ArrowRightIcon aria-hidden color="var(--colors-primary-icon-strong)" size={24} />
                ) : null}
              </Button>
            )}
          </Col>
        </Grid>
      </S.Section>
    )
  }
)

Help.displayName = 'Help'
